import { Tooltip } from "@mui/material";
import { PositionRoleEnum } from "@sinch/core/entities/serverEnums";
import { MdsSwapHorizontalCircle } from "@sinch/icons";
import { PositionRoleEnum as PositionRoleEnumStrings } from "@sinch/types/sinch.types";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";

export interface OriginalRoleIconProps {
  /**
   * Role the worker was originally booked as before the first role-changing move during closing.
   * `null`/`undefined` means no role-changing move happened.
   */
  originalRole?: PositionRoleEnum | PositionRoleEnumStrings | null;
  /**
   * The worker's current (final) role - the role of the position the attendance now belongs to.
   */
  currentRole: PositionRoleEnum | PositionRoleEnumStrings;
}

/**
 * Visual cue shown on an attendance whose pre-shift role differs from its final role, for the two
 * standby cases: a booked standby that ended up working ("Working standby") and a booked regular
 * worker that ended up a standby ("Staff member moved to standby"). Renders nothing otherwise -
 * including for non-standby changes such as worker <-> crewboss.
 */
export const OriginalRoleIcon = ({ originalRole, currentRole }: OriginalRoleIconProps): ReactElement | null => {
  const { t } = useTranslation();

  if (originalRole == null) {
    return null;
  }

  const tooltip =
    originalRole === PositionRoleEnum.Standby && currentRole !== PositionRoleEnum.Standby
      ? t("ClosingForm.workingStandby")
      : originalRole !== PositionRoleEnum.Standby && currentRole === PositionRoleEnum.Standby
        ? t("ClosingForm.movedToStandby")
        : null;

  if (tooltip == null) {
    return null;
  }

  return (
    <Tooltip title={tooltip}>
      <MdsIcon
        fontSize="small"
        icon={MdsSwapHorizontalCircle}
        sx={{ color: ({ palette }) => palette.entity.positionRole[PositionRoleEnum.Standby] }}
      />
    </Tooltip>
  );
};
